import type {ServiceCenterBannerResponse} from '@/api/bffSupport';
import type {Page} from '@/types';

import {useQuery} from '@tanstack/react-query';
import {useBreakpointUp} from '@travel-frontend/core';
import {useOpeningHours} from '@travel-frontend/ui-components';
import {boolean, is, object} from 'valibot';

import {PAGE_HOTELS} from '@/consts';
import {useConfig} from '@/modules/common/context/Config';
import {useQueryParams} from '@/modules/common/context/QueryParams';
import {useLocalSplit} from '@/modules/common/hooks';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useDestination} from '@/modules/common/hooks/useDestination';
import {useHeaderConfig} from '@/modules/common/hooks/useHeaderConfig';
import {useServiceConfig} from '@/modules/common/hooks/useServiceConfig';
import * as bffSupportQueries from '@/modules/common/queries/bffSupport';

const ServiceCenterBannerSplitConfigSchema = object({
  hotellist: object({enabled: boolean()}),
  offerlist: object({enabled: boolean()}),
});

export type UseServiceCenterBannerResult = ServiceCenterBannerResponse & {
  enabled: boolean;
  treatment: string;
  openingHours: string;
  virtualQWidgetUrl?: string;
};

export const useServiceCenterBanner = ({
  page,
  enabled,
}: {
  page: Page;
  enabled: boolean;
}): UseServiceCenterBannerResult => {
  const currentPage = useCurrentPage();
  const config = useConfig();
  const serviceConfig = useServiceConfig();
  const {keyword} = useDestination();
  const split = useLocalSplit<'agentPicture' | 'enhancedAgentPicture'>(
    'FFP_4042_service_center_banner'
  );

  const isDesktop = useBreakpointUp('lg');
  const device = isDesktop ? 'desktop' : 'mobile';

  const virtualQWidgetUrl = config.virtualQ.enabled
    ? config.virtualQ.widgetUrlByPage[currentPage][device]
    : '';

  const splitConfig = is(ServiceCenterBannerSplitConfigSchema, split.config)
    ? split.config
    : undefined;

  // split ready and correct treatment group? else we know we do not need to request agents
  const isShown =
    (page === PAGE_HOTELS
      ? splitConfig?.hotellist.enabled === true
      : splitConfig?.offerlist.enabled === true) &&
    (split.treatment === 'agentPicture' ||
      split.treatment === 'enhancedAgentPicture');

  const [queryParams] = useQueryParams();
  const destinationId = queryParams.destinationId || 'default';
  const travelType = queryParams.travelType;

  const {data} = useQuery({
    enabled: enabled && isShown,
    ...bffSupportQueries.serviceCenterBanner({
      destinationId,
      page,
      travelType,
      device,
    }),
  });

  // Handle parametrised banner text
  let text;

  if (data?.text && keyword) {
    text = data.text.replace(`{{destination}}`, keyword.keyword);
  }

  const {hotline} = useHeaderConfig();
  const fallbackPhoneNumber = hotline?.numbers[isDesktop ? 'lg' : 'sm'];

  const {long} = useOpeningHours(serviceConfig);
  const openingHours = `${long.timeFrom} – ${long.timeUntil}`;

  // @ts-expect-error TS2322
  return {
    phone: fallbackPhoneNumber,
    ...data,
    text: text,
    enabled: enabled && isShown,
    treatment: split.treatment,
    openingHours,
    virtualQWidgetUrl,
  };
};
